import type { ChatSseEvent, RemoteMessage } from "./api.ts";

/**
 * État d'une discussion, calqué sur DefaultDiscussionRepository.kt (app mobile) pour que
 * le chatbot se comporte de la même façon partout.
 */
export interface ChatState {
  online: boolean;
  streaming: boolean;
  /** Question envoyée, pas encore confirmée par le serveur. */
  pendingQuestion: string | null;
  /** Message d'avancement (« Je cherche dans le livre… »). */
  processing: string | null;
  /** Réponse en cours de génération. */
  partialAnswer: string | null;
  messages: RemoteMessage[];
  /** A-t-on vu un `idle` depuis la connexion ? Sinon on a rejoint une réponse en cours. */
  hasSeenIdle: boolean;
}

export const initialChatState: ChatState = {
  online: true,
  streaming: false,
  pendingQuestion: null,
  processing: null,
  partialAnswer: null,
  messages: [],
  hasSeenIdle: false,
};

export type ChatAction =
  | { type: "reset" }
  | { type: "messagesLoaded"; messages: RemoteMessage[] }
  | { type: "questionSent"; question: string }
  | { type: "questionFailed" }
  | { type: "disconnected" }
  | { type: "sse"; event: ChatSseEvent }
  | { type: "messageUpdated"; message: RemoteMessage };

function upsert(messages: RemoteMessage[], incoming: RemoteMessage[]): RemoteMessage[] {
  const byId = new Map(incoming.map((m) => [m.id, m]));
  const updated = messages.map((m) => byId.get(m.id) ?? m);
  const known = new Set(messages.map((m) => m.id));
  return [...updated, ...incoming.filter((m) => !known.has(m.id))];
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "reset":
      return initialChatState;
    case "messagesLoaded":
      return { ...state, messages: action.messages };
    case "questionSent":
      return { ...state, pendingQuestion: action.question, streaming: true };
    case "questionFailed":
      return { ...state, pendingQuestion: null, streaming: false, processing: null, partialAnswer: null };
    case "disconnected":
      return { ...state, online: false, hasSeenIdle: false };
    case "messageUpdated":
      return { ...state, messages: upsert(state.messages, [action.message]) };
    case "sse": {
      const event = action.event;
      switch (event.type) {
        case "connected":
          return { ...state, online: true };
        case "idle":
          return {
            ...state,
            hasSeenIdle: true,
            streaming: false,
            pendingQuestion: null,
            partialAnswer: null,
            processing: null,
          };
        case "processing":
          return {
            ...state,
            streaming: true,
            partialAnswer: null,
            processing: state.hasSeenIdle ? event.content : null,
          };
        case "answerPiece":
          return {
            ...state,
            streaming: true,
            processing: null,
            partialAnswer: state.hasSeenIdle ? (state.partialAnswer ?? "") + event.content : null,
          };
        case "dbUpdated":
          return {
            ...state,
            streaming: false,
            pendingQuestion: null,
            partialAnswer: null,
            processing: null,
            messages: upsert(upsert(state.messages, event.updatedMessages), event.newMessages),
          };
      }
    }
  }
}
